/**
 * MCP Tools Integration
 * Connects to available MCP servers for real tool use
 */

import { getFileSystemManager } from './file-system-api.js';

export const MCP_TOOLS = {
  // Filesystem Tools
  filesystem: {
    read_file: {
      name: 'mcp__filesystem__read_file',
      description: 'Read contents of a file',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'File path to read' }
        },
        required: ['path']
      }
    },
    write_file: {
      name: 'mcp__filesystem__write_file',
      description: 'Write content to a file',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'File path' },
          content: { type: 'string', description: 'Content to write' }
        },
        required: ['path', 'content']
      }
    },
    list_directory: {
      name: 'mcp__filesystem__list_directory',
      description: 'List files in a directory',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string', description: 'Directory path' }
        },
        required: ['path']
      }
    },
    search_files: {
      name: 'mcp__filesystem__search_files',
      description: 'Search for files by pattern',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string' },
          pattern: { type: 'string' }
        },
        required: ['path', 'pattern']
      }
    },
    directory_tree: {
      name: 'mcp__filesystem__directory_tree',
      description: 'Get directory tree structure',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string' }
        },
        required: ['path']
      }
    }
  },

  // Computer Use (CUA Driver)
  computer_use: {
    click: {
      name: 'mcp__cua-driver__click',
      description: 'Click at coordinates or element',
      parameters: {
        type: 'object',
        properties: {
          element_index: { type: 'number' },
          x: { type: 'number' },
          y: { type: 'number' }
        }
      }
    },
    type_text: {
      name: 'mcp__cua-driver__type_text',
      description: 'Type text into active element',
      parameters: {
        type: 'object',
        properties: {
          text: { type: 'string' }
        },
        required: ['text']
      }
    },
    get_screen: {
      name: 'mcp__cua-driver__get_accessibility_tree',
      description: 'Get current screen accessibility tree',
      parameters: {
        type: 'object',
        properties: {
          pid: { type: 'number' },
          window_id: { type: 'number' }
        },
        required: ['pid', 'window_id']
      }
    },
    launch_app: {
      name: 'mcp__cua-driver__launch_app',
      description: 'Launch an application',
      parameters: {
        type: 'object',
        properties: {
          name: { type: 'string' }
        },
        required: ['name']
      }
    }
  },

  // Memory/Knowledge Graph
  memory: {
    create_entities: {
      name: 'mcp__memory__create_entities',
      description: 'Create entities in knowledge graph',
      parameters: {
        type: 'object',
        properties: {
          entities: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                entityType: { type: 'string' },
                observations: { type: 'array', items: { type: 'string' } }
              }
            }
          }
        },
        required: ['entities']
      }
    },
    search_nodes: {
      name: 'mcp__memory__search_nodes',
      description: 'Search knowledge graph',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string' }
        },
        required: ['query']
      }
    }
  },

  // Web Search
  web: {
    search: {
      name: 'WebSearch',
      description: 'Search the web',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string' },
          numResults: { type: 'number' }
        },
        required: ['query']
      }
    },
    fetch: {
      name: 'WebFetch',
      description: 'Fetch content from URL',
      parameters: {
        type: 'object',
        properties: {
          url: { type: 'string' }
        },
        required: ['url']
      }
    }
  }
};

/**
 * Tool executor that calls actual MCP tools
 */
export class MCPToolExecutor {
  constructor() {
    this.toolCallbacks = new Map();
  }

  /**
   * Register a tool callback
   */
  registerTool(toolName, callback) {
    this.toolCallbacks.set(toolName, callback);
  }

  /**
   * Execute a tool
   */
  async executeTool(toolName, parameters) {
    console.log(`[MCP] Executing tool: ${toolName}`, parameters);

    // Check if we have a registered callback
    const callback = this.toolCallbacks.get(toolName);
    if (callback) {
      return await callback(parameters);
    }

    // Default implementation for common tools
    return await this.executeDefaultTool(toolName, parameters);
  }

  /**
   * Default tool implementations
   */
  async executeDefaultTool(toolName, parameters) {
    // Ensure parameters is an object
    const params = parameters || {};

    console.log('[MCP] Tool:', toolName, 'Params:', params);

    switch (toolName) {
      case 'mcp__filesystem__read_file':
      case 'readFile':
        return await this.readFile(params.path);

      case 'mcp__filesystem__write_file':
      case 'writeFile':
        if (!params.path) {
          console.error('[MCP] writeFile missing path:', params);
          return { success: false, error: 'Missing path parameter' };
        }
        return await this.writeFile(params.path, params.content || '');

      case 'mcp__filesystem__list_directory':
      case 'listDirectory':
        return await this.listDirectory(params.path || '.');

      case 'mcp__filesystem__create_directory':
      case 'createDirectory':
        if (!params.path) {
          return { success: false, error: 'Missing path parameter' };
        }
        return await this.createDirectory(params.path);

      case 'mcp__filesystem__delete_file':
      case 'deleteFile':
        if (!params.path) {
          return { success: false, error: 'Missing path parameter' };
        }
        return await this.deleteFile(params.path);

      case 'WebSearch':
      case 'searchWeb':
        return await this.webSearch(params.query, params.numResults);

      case 'WebFetch':
      case 'fetchUrl':
        return await this.webFetch(params.url);

      case 'gitStatus':
        return await this.gitStatus(params.path);

      case 'createAgent':
        return {
          success: true,
          message: `Agent ${params.type || 'unknown'} created (simulated)`,
          agentId: `agent-${Date.now()}`
        };

      case 'createPlan':
        return {
          success: true,
          message: 'Plan created (simulated)',
          plan: { goal: params.goal, steps: params.steps || [] }
        };

      default:
        console.warn(`Tool ${toolName} not implemented yet`);
        return {
          success: true,
          message: `Tool ${toolName} executed (simulated)`,
          simulated: true
        };
    }
  }

  // Filesystem implementations (using File System Access API)
  async readFile(path) {
    try {
      const fsm = getFileSystemManager();

      if (!fsm.hasAccess()) {
        return {
          success: false,
          error: 'No directory access. User needs to grant permission first.',
          needsPermission: true
        };
      }

      return await fsm.readFile(path);
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  async writeFile(path, content) {
    try {
      const fsm = getFileSystemManager();

      // If no permission, will auto-download as fallback
      return await fsm.writeFile(path, content);
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  async listDirectory(path) {
    try {
      const fsm = getFileSystemManager();

      if (!fsm.hasAccess()) {
        return {
          success: false,
          error: 'No directory access. User needs to grant permission first.',
          needsPermission: true
        };
      }

      return await fsm.listDirectory(path);
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  async createDirectory(path) {
    try {
      const fsm = getFileSystemManager();

      if (!fsm.hasAccess()) {
        return {
          success: false,
          error: 'No directory access. User needs to grant permission first.',
          needsPermission: true
        };
      }

      return await fsm.createDirectory(path);
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  async deleteFile(path) {
    try {
      const fsm = getFileSystemManager();

      if (!fsm.hasAccess()) {
        return {
          success: false,
          error: 'No directory access. User needs to grant permission first.',
          needsPermission: true
        };
      }

      return await fsm.delete(path);
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  async webSearch(query, numResults = 5) {
    try {
      // Use DuckDuckGo instant answer API
      const response = await fetch(`https://api.duckduckgo.com/?q=${encodeURIComponent(query)}&format=json`);
      const data = await response.json();

      return {
        success: true,
        results: data.RelatedTopics.slice(0, numResults).map(topic => ({
          title: topic.Text,
          url: topic.FirstURL,
          snippet: topic.Text
        })),
        query
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  async webFetch(url) {
    try {
      const response = await fetch(url);
      const content = await response.text();

      return {
        success: true,
        content: content.substring(0, 5000), // Limit size
        url,
        status: response.status
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
}

/**
 * Get all available tools as Anthropic tool format
 */
export function getAnthropicTools() {
  const tools = [];

  // Flatten all MCP tools
  for (const [category, categoryTools] of Object.entries(MCP_TOOLS)) {
    for (const [name, tool] of Object.entries(categoryTools)) {
      tools.push({
        name: tool.name,
        description: tool.description,
        input_schema: tool.parameters
      });
    }
  }

  return tools;
}
